import React from 'react';
import {
  ExternalData,
  FbeForm,
  FbeLoop,
  FbeQuestion,
  FbeQuestionFeature,
  FbeQuestionLoopRepeatType,
  FbeQuestionType,
  FbeSection,
} from '../../services/fbeTypes';
import { Grid2, Tooltip } from '@mui/material';
import { getLocalizedText } from '../../services/service';

type StringKeyValue = {
  [key: string]: string;
};

const specialInfo :StringKeyValue = {
  AL: 'AL = alternative question',
  I: 'I = inactive',
  L: 'L = loop',
  LA: 'LA = after loop',
  LB: 'LB = before loop',
  LR: 'LR = repeat (loop)',
  MA: 'MA = must answer',
  MS: 'MS = must score',
  T: 'T = manipulate in table',
  U: 'U = unique',
}

const dependencyInfo :StringKeyValue = {
  D: 'D = dependency',
  ED: 'ED = external dependency',
  EN: 'EV = external variable (value)',
  ID: 'ID = internal dependency',
  IN: 'IV = internal variable (value)',
  O: 'O = internal output variable',
  ME: 'ME = tolerance external min variable',
  MI: 'MI = tolerance internal min variable',
  AE: 'AE = tolerance external max variable',
  AI: 'AI = tolerance internal max variable',
}

export interface FormPreviewProps {
  form: FbeForm | undefined;
  sections: FbeSection[] | null | undefined;
  parentForm: FbeForm | undefined;
  parentSections: FbeSection[] | null | undefined;
  language: string;
}

const translateQuestionType = (questionType: FbeQuestionType): string => {
  switch (questionType) {
    case FbeQuestionType.DEADLINE:
      return 'Deadline';
    case FbeQuestionType.INFO:
      return 'Info';
    case FbeQuestionType.FREE:
      return 'Free';
    case FbeQuestionType.NUMBER:
      return 'Number';
    case FbeQuestionType.NUMBER_CORRECTION:
      return 'Number correction';
    case FbeQuestionType.NUMBER_HIDDEN:
      return 'Number (hidden)';
    case FbeQuestionType.NUMBER_TABLE:
      return 'Number table';
    case FbeQuestionType.PICTURE:
      return 'Picture';
    case FbeQuestionType.CHOICE:
      return 'Choice';
    case FbeQuestionType.MULTI_SELECT:
      return 'Multi select';
    case FbeQuestionType.SELECT:
      return 'Select';
    case FbeQuestionType.SELECT_ONO:
      return 'Select Ok/ NotOk';
    case FbeQuestionType.SELECT_ONONA:
      return 'Select Ok/ NotOk/ Na';
    case FbeQuestionType.SELECT_WARNING_ONONA:
      return 'Select Ok/ NotOk/ WarningNa';
    case FbeQuestionType.SELECT_YN:
      return 'Select Yes/ No';
    case FbeQuestionType.SELECT_YN_CHOICE:
      return 'Select Yes/ No Choice';
    case FbeQuestionType.SELECT_YNNAE:
      return 'Select Yes/ No/ Na/ Existing';
    case FbeQuestionType.SIGNATURE:
      return 'Signature';
    case FbeQuestionType.TEXT:
      return 'String';
    case FbeQuestionType.HEADER:
      return 'Header';
    case FbeQuestionType.WARNING:
      return 'Warning';
    case FbeQuestionType.SAFETY_YES:
      return 'Safety Yes';
    case FbeQuestionType.SAFETY_YES_NO:
      return 'Safety Yes/ No';
    default:
      return 'Unknown';
  }
};

const QuestionItem = (
  sectionName: string,
  active: boolean,
  index: number,
  question: FbeQuestion,
  externalData: ExternalInfo,
  loopInfo: string,
  language: string,
): JSX.Element => {
  const special: string[] = [];
  let specialAdditional: StringKeyValue = {};

  const dependency: string[] = [];
  let dependencyAdditional: StringKeyValue = {};

  if(!active){
    special.push('I');
  }

  if(loopInfo.length > 0){
    special.push('L');
    specialAdditional = {
      ...specialAdditional,
      L: `Loop:\n${loopInfo}\n `
    }
}

  if(question.mustScore){
    special.push('MS');
  }

  if(question.features && question.features.length > 0){
    question.features.map(feature => {
      switch(feature){
        case FbeQuestionFeature.MANIPULATE_IN_TABLE:
          special.push('T');
          break;
        case FbeQuestionFeature.MUST_ANSWER:
          special.push('MA');
          break;
        case FbeQuestionFeature.UNIQUE:
          special.push('U');
          break;
        default:
          break;
      }
    });
  }

  if(loopInfo.length > 0 && question.loopRepeat){
    switch(question.loopRepeat){
      case FbeQuestionLoopRepeatType.AFTER:
        special.push('LA');
        break;
      case FbeQuestionLoopRepeatType.BEFORE:
        special.push('LB');
        break;
      case FbeQuestionLoopRepeatType.REPEAT:
        special.push('LR');
        break;
      default:
        break;
    }
  }

  if (question.externalDependency && question.externalDependency.length > 0) {
    const questionDependencies = question.externalDependency;

    let temp = '';
    dependency.push('ED');

    questionDependencies.map(dependency => {
      temp += `dependency external variable:\n ${externalData[dependency.variable]}\n `;
      if(dependency.compareValue){
        temp += `dependency external compare value = ${dependency.compareValue}\n`;
      }
      if(dependency.compareVariable){
        temp += `dependency external compare variable:\n ${externalData[dependency.compareVariable]}\n `;
      }
    });
    dependencyAdditional = {
      ...dependencyAdditional,
      ED: temp
    }
  }

  if (question.internalDependency && question.internalDependency.length > 0) {
    const questionDependencies = question.internalDependency;

    let temp = '';
    dependency.push('ID');

    questionDependencies.map(dependency => {
      temp += `dependency internal variable:\n ${externalData[dependency.variable]}\n `;
      if(dependency.compareValue){
        temp += `dependency internal compare value = ${dependency.compareValue}\n`;
      }
      if(dependency.compareVariable){
        temp += `dependency internal compare variable:\n ${externalData[dependency.compareVariable]}\n `;
      }
    });
    dependencyAdditional = {
      ...dependencyAdditional,
      ID: temp
    }
  }

  if(question.externalVariable){
    dependency.push('EN');
    let temp = `dependency external variable:\n ${externalData[question.externalVariable]}\n `;
    temp += `input external variable (value) = ${question.externalVariable}\n `
    dependencyAdditional = {
      ...dependencyAdditional,
      EN: temp
    }
  }

  if(question.internalVariable){
    dependency.push('IN');
    dependencyAdditional = {
      ...dependencyAdditional,
      EN: `input internal variable (value) = ${question.internalVariable}\n `
    }
  }

  if(question.internalOutputVariable){
    dependency.push('O');
    dependencyAdditional = {
      ...dependencyAdditional,
      O: `output internal variable (value) = ${question.internalOutputVariable}\n `
    }
  }

  if(question.toleranceMinExternalVariable){
    dependency.push('ME');
    let temp = `dependency external variable:\n ${externalData[question.toleranceMinExternalVariable]}\n `;
    temp += `tolerance min external variable = ${question.toleranceMinExternalVariable}\n `
    dependencyAdditional = {
      ...dependencyAdditional,
      ME: temp
    }
  }

  if(question.toleranceMinInternalVariable){
    dependency.push('MI');
    dependencyAdditional = {
      ...dependencyAdditional,
      MI: `tolerance min internal variable = ${question.toleranceMinInternalVariable}\n `
    }
  }

  if(question.tolerancePlusExternalVariable){
    dependency.push('AE');
    let temp = `dependency external variable:\n ${externalData[question.tolerancePlusExternalVariable]}\n `;
    temp += `tolerance max external variable = ${question.tolerancePlusExternalVariable}\n `
    dependencyAdditional = {
      ...dependencyAdditional,
      AE: temp
    }
  }

  if(question.tolerancePlusInternalVariable){
    dependency.push('AI');
    dependencyAdditional = {
      ...dependencyAdditional,
      AI: `tolerance max internal variable = ${question.tolerancePlusInternalVariable}\n `
    }
  }

  if(question.alternativeQuestionType){
    special.push('AL');
    specialAdditional = {
      ...specialAdditional,
      AL: `${translateQuestionType(question.alternativeQuestionType)}}\n `
    }
  }
  const questionType = translateQuestionType(question.questionType);
  const dataTestId = `preview-data-${index}`;

  const isParent = (question.timeIndication === PARENT_ID);
  const color = isParent ? 'lightgray' : 'black';

  return (
    <Grid2
      container
      size={{ xs: 12 }}
      spacing={2}
      sx={{
//test1        fontSize: '14px',
//test1        height: '20px',
//test2        overflowX: 'scroll',
        paddingLeft: '7px',
        color: `${color}`,
      }}
      data-testid={dataTestId}
    >
      <Grid2 size={{ xs: 2 }}>{sectionName}</Grid2>
      <Grid2 size={{ xs: 1 }}>{index} ({question.order >= 10000 ? question.order- 10000 : question.order})</Grid2>
  <Grid2 size={{ xs: 4 }}>
    <span
      className="preview-html"
      style={{ display: 'block', whiteSpace: 'normal', overflowWrap: 'anywhere' }}
      dangerouslySetInnerHTML={{ __html: getLocalizedText(question.question, language) }}
    />
  </Grid2>
      <Grid2 size={{ xs: 2 }}>{questionType}</Grid2>
      <Grid2 size={{ xs: 1 }}>
      {special.map( (specialElement, index) => {
        return (
          <>
          <Tooltip
            title={specialAdditional[specialElement] || specialInfo[specialElement]}
            componentsProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'pre-line',
                },
              },
            }}
            >
          <b>{specialElement}</b>
          </Tooltip>
          {(index + 1) < special.length ? ' ' : ''}
          </>
        )
      })
      }
      </Grid2>
      <Grid2 size={{ xs: 2 }}>
      {dependency.map( (dependencyElement, index) => {
        return (
          <>
          <Tooltip
            title={dependencyAdditional[dependencyElement] ||dependencyInfo[dependencyElement]}
            componentsProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'pre-line',
                },
              },
            }}
            >
          <b>{dependencyElement}</b>
          </Tooltip>
          {(index + 1) < dependency.length ? ' ' : ''}
          </>
        )
      })
      }
      </Grid2>
    </Grid2>
  );
};

export interface SectionsItemsProps {
  sections: FbeSection[];
  externalInfo: ExternalInfo;
  language: string;
}

const SectionSeparator = (): JSX.Element => (
  <Grid2
    container
    size={{ xs: 12 }}
    spacing={2}
    sx={{ fontSize: '14px', height: '20px' }}
  >
    <Grid2 size={{ xs: 12 }}>
      <div style={{ paddingLeft: '0px', paddingRight: '0px' }}>
        <hr
          style={{
            border: '0',
            borderTop: '1px solid #d3d3d3',
            margin: 0,
            width: '100%',
          }}
        />
      </div>
    </Grid2>
  </Grid2>
);

type ExternalInfo = { [key: string]: string };

const prepareExternalData = (
  externalDataList: ExternalData[]
): ExternalInfo => {
  if (externalDataList.length === 0) {
    return {};
  }

  const result: ExternalInfo = {};

  externalDataList.map((externalData) => {
    externalData.mapping.map((singleMapping) => {
      const key = singleMapping.dest;
      let value = `source=${singleMapping.source}`;
      value += `${
        singleMapping.dest ? '\n dest=' + singleMapping.dest : ''
      }`;
      value += `${
        singleMapping.operator ? '\n operator=' + singleMapping.operator : ''
      }`;
      value += `${
        singleMapping.compareValue
          ? '\n compare=' + singleMapping.compareValue
          : ''
      }`;
      value += `${
        singleMapping.default ? '\n default=' + singleMapping.default : ''
      }`;
      value += `${singleMapping.type ? '\n type=' + singleMapping.type : ''}`;
      result[key] = value;
    });
  });

  return result;
};

const getLoopInfo = (
  loop: FbeLoop | undefined,
  language: string
): string => {
  if(!loop){ return '' }

  const hasExternalVariable = loop.externalVariable && loop.externalVariable !== '';
  let variableInfo = hasExternalVariable ? `\n variable (total) = ${loop.externalVariable || ''}` : '';

  const hasInternalVariable = loop.ascendingInternalVariable && loop.ascendingInternalVariable !== '';
  variableInfo += hasInternalVariable ? `\n variable (order) = ${loop.ascendingInternalVariable || ''}\n value (order) = ${loop.ascendingInternalValue || ''}` : '';
  return `name = ${getLocalizedText(loop.name, language)}\n total = ${loop.total}\n ascending = ${loop.ascending}${variableInfo}`
}

const SectionsItems = (props: SectionsItemsProps): JSX.Element => {
  const sections = props.sections;
  const externalInfo = props.externalInfo;
  const language = props.language;

  if (sections.length === 0) {
    return <></>;
  }

  const rows: JSX.Element[] = [];

  rows.push(SectionSeparator());

  let index = 0;
  sections.map((section) => {
    const sortedQuestions =
      section.questions.length > 0
        ? section.questions.sort((a, b) => a.order - b.order)
        : [];

    if (sortedQuestions.length > 0) {
      sortedQuestions.map((question) => {
        const questionRow = QuestionItem(
          getLocalizedText(section.name, language),
          section.active,
          index++,
          question,
          externalInfo,
          getLoopInfo(section.loop, language),
          language
        );
        rows.push(questionRow);
      });
      rows.push(SectionSeparator());
    }

  });

  return (
    <>
      {rows}
    </>
  );
};

const PARENT_ID = '1';
const NEW_ID = '2';
// Add scoped CSS for nested lists in preview HTML
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    .preview-html ol { list-style-type: decimal; margin-left: 1.5em; }
    .preview-html ol ol { list-style-type: lower-alpha; }
    .preview-html ol ol ol { list-style-type: lower-roman; }
    .preview-html ul { list-style-type: disc; margin-left: 1.5em; }
    .preview-html ul ul { list-style-type: circle; }
    .preview-html ul ul ul { list-style-type: square; }
  `;
  document.head.appendChild(style);
}

const mergeSections = (
  currentSections: FbeSection[],
  parentSections?: FbeSection[],
): FbeSection[] => {
  if(!parentSections || parentSections.length === 0){
    return currentSections;
  }

  const mergedFormSections: FbeSection[] = [];

  const questionSetIdList: string[] = [];
  parentSections.map(parentSection => {
      questionSetIdList.push(`${parentSection.mergeMetaData?.questionSetId}`);
  });

  const newSections = currentSections.filter(formSection => !questionSetIdList.includes(`${formSection.mergeMetaData?.questionSetId}`) )

  // Update existing sections
  parentSections.map( parentSection => {
    const updatedCurrentSection = currentSections.find(section => section.mergeMetaData?.questionSetId === parentSection.mergeMetaData?.questionSetId)

    const mergedSection = parentSection;

    if(updatedCurrentSection && updatedCurrentSection.questions.length > 0){
      const updatedQuestions = updatedCurrentSection.questions.map(question => {
        return {
          ...question,
          order: 10000 + question.order,
          timeIndication: NEW_ID
        }
      })

      const existingQuestions = parentSection.questions.map(question => {
        return {
          ...question,
          order: question.order,
          timeIndication: PARENT_ID
        }
      })

      mergedSection.questions = [
        ...existingQuestions,
        ...updatedQuestions
      ]
    } else {
      const existingQuestions = parentSection.questions.map(question => {
        return {
          ...question,
          order: question.order,
          timeIndication: PARENT_ID
        }
      })

      mergedSection.questions = [
        ...existingQuestions,
      ]
    }
    mergedFormSections.push(mergedSection);
  });

  // Add new sections
  if(newSections.length > 0){
    newSections.map(section => {
      const updatedQuestions = section.questions.map(question => {
        return {
          ...question,
          order: 10000 + question.order,
          timeIndication: NEW_ID
        }
      })

      mergedFormSections.push({...section, questions: updatedQuestions});
    })
  }

  return mergedFormSections;
}

const FormPreview = (props: FormPreviewProps): JSX.Element => {
  const formSections = JSON.parse(JSON.stringify(props.sections || [])) as FbeSection[];
  const parentSections = JSON.parse(JSON.stringify(props.parentSections || [])) as FbeSection[];

  const sortedSections =
    formSections.length > 0
      ? formSections.sort((a, b) => a.order - b.order)
      : [];

  const mergedSections = mergeSections(sortedSections, parentSections);

  const externalInfo = props.form?.externalData
    ? prepareExternalData(props.form.externalData || {})
    : {};

  return (
    <>
      <Grid2 container spacing={2}>
        <Grid2
          container
          size={{ xs: 12 }}
          spacing={2}
          display={'flex'}
          alignItems={'start'}
          sx={{
            paddingLeft: '7px',
            fontWeight: 'bold',
//            fontSize: '16px',
//            height: '50px',
            width: '100%',
          }}
          data-testid="preview-header"
        >
          <Grid2 size={{ xs: 2 }}>Section</Grid2>
          <Grid2 size={{ xs: 1 }}>Order</Grid2>
          <Grid2 size={{ xs: 4 }}>Question</Grid2>
          <Grid2 size={{ xs: 2 }}>Type</Grid2>
          <Grid2 size={{ xs: 1 }}>Special</Grid2>
          <Grid2 size={{ xs: 2 }}>Dependency</Grid2>
        </Grid2>
        <SectionsItems language={props.language} externalInfo={externalInfo} sections={mergedSections} />
      </Grid2>
    </>
  );
};

export default FormPreview;
